import React from "react";
import {
  BatteryFull,
  ChevronRight,
  Circle,
  Frown,
  Meh,
  MessageSquare,
  Signal,
  Smile,
  Star,
  Wifi,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { PresignedImage } from "@/components/common/PresignedImage";
import { cn } from "@/lib/utils";
import type { PostCheckoutFeedbackSettings } from "@/types/booking-checkout-settings";

interface PostCheckoutFeedbackPreviewProps {
  settings: PostCheckoutFeedbackSettings;
}

const categories = ["purity", "comfort", "service"];
const ratingIcons = [Meh, Smile, Smile, Meh, Frown];

export const PostCheckoutFeedbackPreview = ({
  settings,
}: PostCheckoutFeedbackPreviewProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <aside className="flex min-h-full w-full flex-col items-center bg-muted/30 p-5 lg:max-w-md">
      <h2 className="text-lg font-semibold">
        {t("feedbacks.settings.preview.title")}
      </h2>

      <div className="mt-2 w-[320px] overflow-hidden rounded-[36px] border-[6px] border-black bg-eathy-comfort-5">
        <div className="relative flex h-10 items-center justify-between px-6 text-xs font-semibold text-foreground">
          <span>9:41</span>
          <div className="absolute left-1/2 top-1/2 h-5 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-black" />
          <div className="flex items-center gap-1">
            <Signal className="size-3.5" />
            <Wifi className="size-3.5" />
            <BatteryFull className="size-4" />
          </div>
        </div>

        <div className="flex h-[620px] flex-col overflow-hidden bg-eathy-comfort-5 px-5 pb-4">
          <div className="flex items-center justify-between pt-2">
            <div className="flex size-9 items-center justify-center rounded-full bg-background/80 shadow-sm">
              <div className="size-4 rounded-full border border-eathy-comfort-4" />
            </div>
            <h3 className="text-base font-semibold tracking-normal text-foreground">
              {t("feedbacks.settings.preview.phoneTitle")}
            </h3>
            <div className="flex h-9 items-center gap-2 rounded-full bg-background px-3 text-xs font-medium text-foreground shadow-sm">
              <Circle className="size-3.5" />
              EN
            </div>
          </div>

          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-background shadow-sm">
            <div className="h-full w-4/5 rounded-full bg-eathy-comfort-2" />
          </div>

          <div className="py-2.5 text-center text-base font-semibold tracking-normal text-foreground">
            Step <span className="text-eathy-comfort-2">4/5</span>
          </div>

          <div
            className={cn(
              "flex h-[150px] items-center justify-center overflow-hidden rounded-[22px] bg-muted text-xs text-muted-foreground",
              !settings.enabled && "opacity-45 grayscale",
            )}
          >
            {settings.mediaId ? (
              <PresignedImage
                fileId={settings.mediaId}
                alt={t("feedbacks.settings.preview.feedbackPhotoAlt")}
                className="size-full object-cover"
              />
            ) : (
              t("feedbacks.settings.form.photoPlaceholder")
            )}
          </div>

          <div
            className={cn(
              "mt-4 flex flex-col items-center gap-1",
              !settings.enabled && "opacity-45",
            )}
          >
            <h3 className="text-center text-base font-semibold text-eathy-comfort-1">
              {settings.enabled
                ? t("feedbacks.settings.preview.enabledHeadline")
                : t("feedbacks.settings.preview.disabledHeadline")}
            </h3>
            <p className="text-center text-xs text-eathy-comfort-1/80">
              {settings.enabled
                ? t("feedbacks.settings.preview.enabledDescription")
                : t("feedbacks.settings.preview.disabledDescription")}
            </p>
          </div>

          <div
            className={cn(
              "mt-2.5 rounded-2xl bg-background px-3 py-2 shadow-sm",
              !settings.enabled && "opacity-45",
            )}
          >
            <p className="text-center text-xs text-eathy-comfort-1/80">
              {t("feedbacks.settings.preview.overallQuestion")}
            </p>
            <div className="mt-2 flex items-center justify-center gap-4">
              {ratingIcons.map((RatingIcon, index) => {
                const rating = index + 1;
                const isSelected = rating === settings.lowRatingThreshold;

                return (
                  <span
                    key={rating}
                    className={cn(
                      "flex size-7 items-center justify-center rounded-full text-eathy-comfort-3",
                      isSelected &&
                        "ring-2 ring-muted-foreground/30 text-royal-glow-1",
                    )}
                  >
                    <RatingIcon className="size-4" />
                  </span>
                );
              })}
            </div>
          </div>

          <div
            className={cn(
              "mt-3 rounded-2xl bg-background px-4 py-3 shadow-sm",
              !settings.enabled && "opacity-45",
            )}
          >
            <div className="flex flex-col gap-2.5">
              {categories.map((category) => (
                <div
                  key={category}
                  className="flex items-center justify-between"
                >
                  <span className="text-sm font-medium text-eathy-comfort-1">
                    {t(`feedbacks.settings.preview.categories.${category}`)}
                  </span>
                  <span className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <Star
                        key={rating}
                        className={cn(
                          "size-4 fill-current",
                          rating < 5
                            ? "text-amber-400"
                            : "text-muted-foreground/20",
                        )}
                      />
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {settings.externalReviewsEnabled && settings.enabled && (
            <div className="mt-3 flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 rounded-full bg-background px-3 text-xs font-medium shadow-sm"
              >
                <MessageSquare className="size-3.5" />
                {t("feedbacks.settings.preview.addComment")}
              </Button>
            </div>
          )}

          <div className="mt-auto flex flex-col gap-2 pt-3">
            <Button
              type="button"
              size="sm"
              disabled={!settings.enabled}
              className="h-10 rounded-2xl bg-royal-glow-1 text-sm font-medium text-white hover:bg-royal-glow-1/90"
            >
              {t("feedbacks.settings.preview.submit")}
              <ChevronRight className="size-4" />
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-10 rounded-2xl bg-background text-sm font-medium text-foreground shadow-sm hover:bg-background"
            >
              {t("feedbacks.settings.preview.skip")}
            </Button>
            <div className="mx-auto mt-1 h-1 w-24 rounded-full bg-black" />
          </div>
        </div>
      </div>
    </aside>
  );
};
